import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, AlertTriangle } from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import { Ticket } from '../../../types/response'
import { ERROR_SERVICE_INCIDENT_TICKET_KEY } from '../../../constants'

interface TicketContentProps {
    ticket: Ticket
    /** When true, renders as section within parent card (no Paper wrapper) */
    asSection?: boolean
}

const TicketContent: React.FC<TicketContentProps> = ({ ticket, asSection }) => {
    const { t } = useTranslation()

    const showAffected =
        ticket?.ticket_type_key === ERROR_SERVICE_INCIDENT_TICKET_KEY

    const content = (
        <div className={asSection ? 'p-3 space-y-3' : 'space-y-3'}>
            {/* Ticket Description */}
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <Typogaphy className="text-sm font-medium text-gray-700">
                        {t('ticket-description')}
                    </Typogaphy>
                </div>
                <div
                    className="max-h-[250px] overflow-y-auto border border-gray-100 rounded-md p-3 bg-gray-50/50 prose prose-xs max-w-none text-sm [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-medium [&_h2]:font-medium [&_h3]:font-medium [&_p]:text-sm [&_li]:text-sm"
                    dangerouslySetInnerHTML={{
                        __html: ticket?.ticket_content || '',
                    }}
                />
            </div>

            {/* Affected Section - for error_service_incident */}
            {showAffected && (
                <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <Typogaphy className="text-sm font-medium text-red-700">
                            {t('ticket-affected-info')}
                        </Typogaphy>
                    </div>
                    {ticket?.ticket_affected ? (
                        <div
                            className="max-h-[200px] overflow-y-auto border border-red-100 rounded-md p-3 bg-red-50/50 prose prose-xs max-w-none text-sm [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-medium [&_h2]:font-medium [&_h3]:font-medium [&_p]:text-sm [&_li]:text-sm"
                            dangerouslySetInnerHTML={{
                                __html: ticket.ticket_affected || '',
                            }}
                        />
                    ) : (
                        <div className="border border-gray-100 rounded-md p-3 bg-gray-50/50 text-gray-400 text-sm italic">
                            {t('no-information')}
                        </div>
                    )}
                </div>
            )}
        </div>
    )

    if (asSection) {
        return content
    }

    return <Paper className="p-4s">{content}</Paper>
}

export default TicketContent
